import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import { FaCalendarAlt, FaUser, FaClock, FaArrowRight, FaSearch, FaFilter } from 'react-icons/fa';
import formService from '../services/formService';
import blogService from '../services/blogService';
import defaultBlogImage from '../pics/FOOT ICON2.svg';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load blog posts from API
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const data = await blogService.getAllPosts();
        // Normalize blog posts - parse tags if they're JSON strings, ensure proper date format
        const normalizedPosts = data.map(post => ({
          ...post,
          tags: typeof post.tags === 'string' ? JSON.parse(post.tags || '[]') : (post.tags || []),
          date: post.date || post.created_at,
          readTime: post.readTime || post.read_time || '5 min read',
          image_url: post.image_url || post.image
        }))
        // Filter out test posts and elderly posts
        .filter(post => {
          const titleLower = (post.title || '').toLowerCase();
          const slugLower = (post.slug || '').toLowerCase();
          const authorLower = (post.author || '').toLowerCase();
          return !titleLower.includes('test') && 
                 !slugLower.includes('test') && 
                 !authorLower.includes('test') &&
                 !titleLower.includes('elderly') &&
                 !titleLower.includes('elderly care');
        });
        setBlogPosts(normalizedPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        // Keep empty array on error
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  // Newsletter submission handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setIsNewsletterSubmitting(true);
    setNewsletterStatus(null);

    try {
      const result = await formService.subscribeToNewsletter(newsletterEmail);
      setNewsletterStatus('success');
      setNewsletterEmail('');
      console.log('Newsletter subscription successful:', result);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  // Sample fallback data
  const sampleBlogPosts = [
    {
      id: 1,
      title: "Understanding Plantar Fasciitis: Causes and Treatment",
      excerpt: "Plantar fasciitis is one of the most common causes of heel pain. Learn about the causes, symptoms, and effective treatment options for this condition.",
      content: "Plantar fasciitis is a common foot condition that causes pain in the heel and bottom of the foot. It occurs when the plantar fascia, a thick band of tissue that connects the heel bone to the toes, becomes inflamed...",
      author: "Dr. Sarah Johnson",
      date: "2024-01-15",
      readTime: "5 min read",
      category: "Foot Health",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop",
      tags: ["plantar fasciitis", "heel pain", "foot care"],
      featured: true
    },
    {
      id: 2,
      title: "The Importance of Proper Footwear for Diabetic Patients",
      excerpt: "Choosing the right footwear is crucial for diabetic patients to prevent complications and maintain foot health. Discover what to look for in diabetic-friendly shoes.",
      content: "Diabetes can cause nerve damage and poor blood circulation in the feet, making proper footwear essential for preventing serious complications...",
      author: "Dr. Michael Chen",
      date: "2024-01-10",
      readTime: "4 min read",
      category: "Diabetic Care",
      image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=250&fit=crop",
      tags: ["diabetes", "footwear", "prevention"],
      featured: false
    },
    {
      id: 3,
      title: "Home Exercises for Stronger Feet and Ankles",
      excerpt: "Strengthen your feet and ankles with these simple exercises you can do at home. Improve balance, stability, and overall foot health.",
      content: "Strong feet and ankles are essential for maintaining balance, preventing injuries, and supporting overall mobility. Here are some effective exercises...",
      author: "Dr. Emma Wilson",
      date: "2024-01-05",
      readTime: "6 min read",
      category: "Exercise",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop",
      tags: ["exercises", "strength", "balance"],
      featured: false
    },
    {
      id: 4,
      title: "Common Foot Problems in the Elderly and How to Address Them",
      excerpt: "As we age, our feet undergo changes that can lead to various problems. Learn about common issues and how to maintain foot health in later years.",
      content: "Aging affects every part of our body, including our feet. Understanding these changes and taking proactive steps can help maintain foot health...",
      author: "Dr. Robert Davis",
      date: "2023-12-28",
      readTime: "7 min read",
      category: "Elderly Care",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=250&fit=crop",
      tags: ["elderly", "aging", "prevention"],
      featured: false
    },
    {
      id: 5,
      title: "The Benefits of Custom Orthotics for Foot Pain Relief",
      excerpt: "Custom orthotics can provide significant relief for various foot conditions. Discover how these personalized inserts can improve your foot health.",
      content: "Custom orthotics are medical devices designed to support and align the foot and ankle. They can provide relief for various conditions...",
      author: "Dr. Lisa Thompson",
      date: "2023-12-20",
      readTime: "5 min read",
      category: "Orthotics",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop",
      tags: ["orthotics", "pain relief", "custom"],
      featured: false
    },
    {
      id: 6,
      title: "Preventing Athlete's Foot: Tips for Healthy Feet",
      excerpt: "Athlete's foot is a common fungal infection that can be prevented with proper foot hygiene. Learn effective prevention strategies.",
      content: "Athlete's foot, also known as tinea pedis, is a fungal infection that commonly affects the skin between the toes...",
      author: "Dr. James Miller",
      date: "2023-12-15",
      readTime: "4 min read",
      category: "Foot Health",
      image: "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=250&fit=crop",
      tags: ["athlete's foot", "fungal infection", "prevention"],
      featured: false
    }
  ];

  // Get unique categories from posts
  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];

  // Filter blog posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const tags = Array.isArray(post.tags) ? post.tags : [];
    const matchesSearch = !searchTerm || 
                         post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tags.some(tag => String(tag).toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // SEO structured data for Blog page
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Drosh The Pod Blog",
    "description": "Expert podiatry advice, foot health tips, and professional insights from our experienced podiatrists.",
    "url": "https://www.droshpod.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.droshpod.com/logo.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "datePublished": post.date || post.created_at,
      "image": post.image_url || post.image || defaultBlogImage,
      "url": `https://www.droshpod.com/blog/${post.id}`
    }))
  };

  return (
    <>
      <SEOHead 
        title="Podiatry Blog | Expert Foot Health Advice - Drosh The Pod"
        description="Expert podiatry advice, foot health tips, and professional insights from our experienced podiatrists. Stay informed about foot care and treatment options."
        keywords="podiatry blog, foot health advice, podiatrist tips, foot care blog, manchester podiatry, foot health articles"
        canonical="https://www.droshpod.com/blog"
        structuredData={blogStructuredData}
      />
      
      <main className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-6">
          <PageBreadcrumbs />
        </div>

        {/* Hero Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Podiatry Blog
                </span>
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Expert advice, foot health tips, and professional insights from our experienced podiatrists. 
                Stay informed about the latest in foot care and treatment options.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="py-8 bg-white/50 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Search Bar */}
                <div className="relative flex-1">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  />
                </div>

                {/* Category Filter */}
                <div className="relative">
                  <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="pl-10 pr-8 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent transition-colors appearance-none bg-white"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {filteredPosts.filter(post => post.featured).length > 0 && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-2xl font-bold text-gray-800 mb-8"
                >
                  Featured Article
                </motion.h2>
                
                {filteredPosts.filter(post => post.featured).map(post => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="relative h-64 lg:h-full bg-gray-100">
                        <img
                          src={post.image_url || post.image || defaultBlogImage}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { 
                            if (e.target.src !== defaultBlogImage) {
                              e.target.src = defaultBlogImage; 
                            }
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
                            Featured
                          </span>
                        </div>
                      </div>
                      <div className="p-8 flex flex-col justify-center">
                        <div className="flex items-center text-sm text-gray-500 mb-4">
                          <FaCalendarAlt className="mr-2" />
                          {(post.date || post.created_at) && new Date(post.date || post.created_at).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                          <FaClock className="ml-4 mr-2" />
                          {post.readTime || post.read_time || '5 min read'}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <FaUser className="mr-2" />
                            {post.author}
                          </div>
                          <Link
                            to={`/blog/${post.slug || post.id}`}
                            className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors"
                          >
                            Read More
                            <FaArrowRight className="ml-2" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Blog Posts Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-2xl font-bold text-gray-800 mb-8"
              >
                Latest Articles
              </motion.h2>

              {filteredPosts.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <p className="text-gray-600 text-lg">No articles found matching your criteria.</p>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.filter(post => !post.featured).map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                    >
                      <div className="relative h-48 bg-gray-100">
                        <img
                          src={post.image_url || post.image || defaultBlogImage}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { 
                            if (e.target.src !== defaultBlogImage) {
                              e.target.src = defaultBlogImage; 
                            }
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-secondary text-white px-2 py-1 rounded-full text-xs font-semibold">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center text-xs text-gray-500 mb-3">
                          <FaCalendarAlt className="mr-1" />
                          {(post.date || post.created_at) && new Date(post.date || post.created_at).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                          <FaClock className="ml-3 mr-1" />
                          {post.readTime || post.read_time || '5 min read'}
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <FaUser className="mr-1" />
                            {post.author}
                          </div>
                          <Link
                            to={`/blog/${post.slug || post.id}`}
                            className="inline-flex items-center text-primary text-sm font-semibold hover:text-secondary transition-colors"
                          >
                            Read More
                            <FaArrowRight className="ml-1" />
                          </Link>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="py-16 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl font-bold text-white mb-4"
              >
                Stay Updated with Foot Health Tips
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/90 mb-8"
              >
                Subscribe to our newsletter for the latest podiatry advice and foot health tips delivered to your inbox.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="max-w-md mx-auto"
              >
                {newsletterStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4">
                    <p className="font-semibold">Thank you for subscribing!</p>
                    <p>You'll receive our latest foot health tips and updates.</p>
                  </div>
                )}

                {newsletterStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
                    <p className="font-semibold">Subscription failed.</p>
                    <p>Please try again or contact us directly.</p>
                  </div>
                )}

                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  />
                  <button 
                    type="submit"
                    disabled={isNewsletterSubmitting}
                    className="bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isNewsletterSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Blog; 